<template>
  <div class="foodleap-about antialiased">

    <!-- Hero -->
    <header class="relative pt-32 pb-20 fa-hero-gradient overflow-hidden">
      <div class="max-w-7xl mx-auto px-8 relative z-10">
        <div class="tw-flex flex-col items-center text-center max-w-4xl mx-auto">
          <span
            class="fa-hero-badge tw-inline-flex items-center gap-2 px-4 py-1.5 tw-flex flex-row rounded-full bg-primary-green/10 text-primary-green text-xs font-bold tracking-widest uppercase mb-6">
            <Icon name="eco" size="16"/>
            Our story
          </span>
          <h1 class="fa-hero-title text-5xl md:text-7xl font-bold tracking-tight text-primary-slate mb-8 leading-[1.1]">
            Building the intelligence layer for the food transition
          </h1>
          <p class="fa-hero-sub text-xl text-primary-slate/70 font-light max-w-2xl mb-12">
            Foodleap connects innovators, infrastructure providers, investors, and policymakers to accelerate the shift towards a sustainable food system.
          </p>
          <!--          <div class="fa-hero-btns tw-flex flex-wrap justify-center gap-4">
                      <button @click="goToRegistration"
                              class="fa-btn-primary bg-primary-slate text-white px-10 py-4 rounded-full font-bold shadow-xl transition-all tw-flex items-center gap-2">
                        Explore the platform
                        <Icon name="arrow-right" class="fa-arrow-icon"/>
                      </button>
                      <button @click="$router.push('/intelligence')" class="fa-btn-secondary bg-brand-light-grey text-primary-slate px-10 py-4 rounded-full font-bold transition-all">
                        View plans
                      </button>
                    </div>-->
        </div>
      </div>
      <div class="fa-blob-1 absolute -top-24 -right-24 w-96 h-96 bg-secondary-blue/10 rounded-full blur-3xl pointer-events-none"></div>
      <div class="fa-blob-2 absolute -bottom-24 -left-24 w-96 h-96 bg-primary-green/10 rounded-full blur-3xl pointer-events-none"></div>
    </header>

    <!-- Mission -->
    <section class="py-24 bg-white">
      <div class="max-w-7xl mx-auto px-8">
        <div class="tw-flex flex-col lg:flex-row items-center gap-20">
          <div class="flex-1 animate-on-scroll">
            <span class="text-primary-slate/40 font-black tracking-widest text-xs uppercase mb-4 block">Our mission</span>
            <h2 class="text-4xl md:text-5xl font-bold tracking-tight text-primary-slate leading-[1.2] mb-6">
              Turning ecosystem data into strategic advantage
            </h2>
            <p class="text-lg text-primary-slate/70 font-light leading-relaxed mb-6">
              The global food system is undergoing its most significant transformation in generations. Yet the intelligence needed to navigate this shift where technologies are
              emerging, who is scaling, where capital is flowing remains fragmented and inaccessible.
            </p>
            <p class="text-lg text-primary-slate/70 font-light leading-relaxed">
              Foodleap was built to change that. We aggregate and synthesize signals from 1000+ organisations across the food innovation landscape, making strategic clarity
              available to every team working on the transition.
            </p>
          </div>
          <div class="flex-1 grid grid-cols-2 gap-4 animate-on-scroll delay-2">
            <div v-for="(stat, i) in stats" :key="i" class="fa-stat-card bg-brand-light-grey/30 rounded-lg p-8 border border-brand-light-grey">
              <div class="text-4xl font-black text-primary-slate mb-2">{{ stat.value }}</div>
              <div class="text-sm text-primary-slate/60 leading-snug">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- What we do -->
    <section class="py-24 bg-brand-light-grey/20 border-y border-brand-light-grey">
      <div class="max-w-7xl mx-auto px-8">
        <div class="text-center mb-20 animate-on-scroll">
          <span class="text-primary-slate/40 font-black tracking-widest text-xs uppercase mb-4 block">What we build</span>
          <h2 class="text-4xl md:text-5xl font-bold text-primary-slate">Three ways we drive the transition</h2>
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          <div v-for="(pillar, i) in pillars" :key="i"
               class="fa-pillar-card animate-on-scroll bg-white rounded-lg p-10 border border-brand-light-grey"
               :class="'delay-' + (i + 1)">
            <div class="w-14 h-14 rounded-xl tw-flex items-center justify-center mb-8"
                 :class="pillar.bgClass">
              <Icon :name="pillar.icon" size="24" :class="pillar.iconClass"/>
            </div>
            <h3 class="text-xl font-bold text-primary-slate mb-3">{{ pillar.title }}</h3>
            <p class="text-primary-slate/60 leading-relaxed">{{ pillar.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Team -->
    <!--    <section class="py-24 bg-white">
          <div class="max-w-7xl mx-auto px-8">
            <div class="tw-flex flex-col md:flex-row justify-between items-end mb-16 gap-6 animate-on-scroll">
              <div>
                <span class="text-primary-slate/40 font-black tracking-widest text-xs uppercase mb-4 block">The people</span>
                <h2 class="text-4xl md:text-5xl font-bold text-primary-slate">Meet the team</h2>
                <p class="text-primary-slate/60 mt-4 max-w-md">A dedicated group of food system strategists, data specialists, and community builders.</p>
              </div>
            </div>
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              <div v-for="(member, i) in team" :key="i"
                   class="fa-team-card animate-on-scroll text-center"
                   :class="'delay-' + (i % 3 + 1)">
                <div class="relative mx-auto mb-4 w-24 h-24">
                  <img
                    :src="member.image"
                    :alt="member.name"
                    class="w-full h-full rounded-full object-cover border-2 border-brand-light-grey"
                    @error="handleImgError"
                  />
                </div>
                <div class="font-bold text-primary-slate text-sm leading-tight mb-1">{{ member.name }}</div>
                <div class="text-xs text-primary-slate/50 leading-snug">{{ member.description }}</div>
              </div>
            </div>
          </div>
        </section>-->

    <!-- Values -->
    <section class="py-24 bg-primary-slate relative overflow-hidden">
      <div class="max-w-7xl mx-auto px-8 relative z-10">
        <div class="text-center mb-20 animate-on-scroll">
          <span class="text-primary-green font-black tracking-widest text-xs uppercase mb-4 block">What we stand for</span>
          <h2 class="text-4xl md:text-5xl font-bold text-white mb-6">Our values</h2>
          <p class="text-white/60 font-light text-lg max-w-2xl mx-auto">Foodleap adheres to four core values. These are visible in everything that we do: both in terms of
            information provision and design.</p>
        </div>
        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="(value, i) in values" :key="i"
               class="fa-value-card animate-on-scroll bg-white/5 rounded-lg p-8 border border-white/10"
               :class="'delay-' + (i % 4 + 1)">
            <Icon :name="value.icon" size="30" class="text-primary-green mb-6 block"/>
            <h4 class="text-lg font-bold text-white mb-3">{{ value.title }}</h4>
            <p class="text-white/50 text-sm leading-relaxed">{{ value.description }}</p>
          </div>
        </div>
      </div>
      <div class="fa-blob-1 absolute top-0 right-0 w-96 h-96 bg-primary-green/5 rounded-full blur-3xl pointer-events-none"></div>
      <div class="fa-blob-2 absolute bottom-0 left-0 w-96 h-96 bg-secondary-blue/5 rounded-full blur-3xl pointer-events-none"></div>
    </section>

    <!-- CTA -->
    <!--    <section class="py-24 bg-white">
          <div class="max-w-4xl mx-auto px-8 text-center animate-on-scroll">
            <h2 class="text-4xl md:text-5xl font-bold text-primary-slate mb-6 leading-[1.15]">
              Ready to join the transition?
            </h2>
            <p class="text-xl text-primary-slate/60 mb-12 font-light">
              Whether you're an innovator, investor, or industry player, your place in the ecosystem is waiting.
            </p>
            <div class="tw-flex flex-wrap justify-center gap-4">
              <button @click="goToRegistration" class="fa-btn-primary bg-primary-green text-primary-slate px-10 py-5 rounded-full font-bold shadow-xl transition-all">
                Get started for free
              </button>
              <button @click="$router.push('/intelligence')" class="fa-btn-secondary bg-brand-light-grey text-primary-slate px-10 py-5 rounded-full font-bold transition-all">
                Explore plans
              </button>
            </div>
          </div>
        </section>-->

    <!-- Contact -->
    <section id="contact" class="py-24 bg-brand-light-grey/20 border-t border-brand-light-grey">
      <div class="max-w-3xl mx-auto px-8">
        <div class="text-center mb-14 animate-on-scroll">
          <span class="text-primary-slate/40 font-black tracking-widest text-xs uppercase mb-4 block">Get in touch</span>
          <h2 class="text-4xl md:text-5xl font-bold text-primary-slate mb-4">Talk to us</h2>
          <p class="text-lg text-primary-slate/60 font-light max-w-xl mx-auto">
            Interested in exploring the platform, discussing a partnership, or learning more about our intelligence products? We'd love to hear from you.
          </p>
        </div>

        <div class="grid md:grid-cols-2 gap-6 animate-on-scroll delay-1">
          <a href="mailto:info@foodleap.com" @click="trackFoodleapCta('About', 'Email', 'Contact')"
             class="fa-contact-card bg-white rounded-2xl border border-brand-light-grey p-8 tw-flex items-center gap-5 no-underline group">
            <div class="w-12 h-12 rounded-xl bg-primary-green/10 tw-flex items-center justify-center shrink-0 group-hover:bg-primary-green/20 transition-colors">
              <Icon name="email" size="22" class="text-primary-green"/>
            </div>
            <div>
              <p class="text-xs font-bold text-primary-slate/40 uppercase tracking-widest mb-1 m-0">Email</p>
              <p class="text-base font-semibold text-primary-slate group-hover:text-primary-green transition-colors m-0">info@foodleap.com</p>
            </div>
          </a>
          <a href="tel:+32477280200" @click="trackFoodleapCta('About', 'Phone', 'Contact')"
             class="fa-contact-card bg-white rounded-2xl border border-brand-light-grey p-8 tw-flex items-center gap-5 no-underline group">
            <div class="w-12 h-12 rounded-xl bg-primary-green/10 tw-flex items-center justify-center shrink-0 group-hover:bg-primary-green/20 transition-colors">
              <Icon name="phone" size="22" class="text-primary-green"/>
            </div>
            <div>
              <p class="text-xs font-bold text-primary-slate/40 uppercase tracking-widest mb-1 m-0">Phone</p>
              <p class="text-base font-semibold text-primary-slate group-hover:text-primary-green transition-colors m-0">+32 477 280 200</p>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- Products & Services -->
    <section class="py-24 bg-white border-t border-brand-light-grey">
      <div class="max-w-7xl mx-auto px-8">
        <div class="text-center mb-16 animate-on-scroll">
          <span class="text-primary-slate/40 font-black tracking-widest text-xs uppercase mb-4 block">What we offer</span>
          <h2 class="text-4xl md:text-5xl font-bold text-primary-slate">Our products & services</h2>
        </div>
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div @click="onProductClick('Open Innovation Challenges', '/intelligence')"
               class="fa-product-card animate-on-scroll delay-1 bg-brand-light-grey/30 rounded-lg p-8 border border-brand-light-grey group">
            <div class="w-12 h-12 rounded-xl bg-primary-green/10 tw-flex items-center justify-center mb-6">
              <Icon name="light-bulb" size="22" class="text-primary-green"/>
            </div>
            <h4 class="text-lg font-bold text-primary-slate mb-3">Open Innovation Challenges</h4>
            <p class="text-sm text-primary-slate/60 leading-relaxed">Co-design open innovation challenges with startups across the food transition ecosystem.</p>
          </div>
          <div @click="onProductClick('Transition Reports', '/transition-pulse-report')"
               class="fa-product-card animate-on-scroll delay-2 bg-brand-light-grey/30 rounded-lg p-8 border border-brand-light-grey group">
            <div class="w-12 h-12 rounded-xl bg-secondary-yellow/10 tw-flex items-center justify-center mb-6">
              <Icon name="wave-square" size="22" class="text-secondary-yellow"/>
            </div>
            <h4 class="text-lg font-bold text-primary-slate mb-3">Transition Reports</h4>
            <p class="text-sm text-primary-slate/60 leading-relaxed">In-depth reports on each food system transition — covering key players, investment flows, regulatory shifts,
              and emerging opportunities.</p>
          </div>
          <div @click="onProductClick('Transition Advisory', { path: '/about', hash: '#contact' })"
               class="fa-product-card animate-on-scroll delay-4 bg-brand-light-grey/30 rounded-lg p-8 border border-brand-light-grey group">
            <div class="w-12 h-12 rounded-xl bg-primary-slate/10 tw-flex items-center justify-center mb-6">
              <Icon name="hand-shake" size="22" class="text-primary-slate"/>
            </div>
            <h4 class="text-lg font-bold text-primary-slate mb-3">Transition Advisory</h4>
            <p class="text-sm text-primary-slate/60 leading-relaxed">Bespoke strategy work built on ecosystem intelligence — from transition roadmaps to executive coaching.</p>
          </div>
        </div>
      </div>
    </section>

    <SimplifiedFooterFoodleapNew/>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import SimplifiedFooterFoodleapNew from '@/components/SimplifiedFooter/SimplifiedFooterFoodleapNew.vue'
  import Icon from '../Icon/Icon.vue'
  import { createScrollDepthTracker, trackFoodleapCta, trackFoodleapInteraction, trackFoodleapPageView, } from '@/util/useFoodleapTracking.js'

  export default defineComponent({
    name: 'StaticHomeFoodleapAbout',
    components: { SimplifiedFooterFoodleapNew, Icon },
    data () {
      return {
        stats: [
          { value: '1000+', label: 'organisations mapped across the ecosystem' },
          { value: '14', label: 'countries represented in the network' },
          { value: '120+', label: 'verified facilities available' },
          { value: '6', label: 'technology transition categories tracked' },
        ],
        pillars: [
          {
            title: 'Ecosystem Intelligence',
            description: 'We track and synthesise signals from 1000+ organisations, startups, corporates, research institutes, and investors into actionable market intelligence.',
            icon: 'compass-2',
            bgClass: 'bg-primary-green/10',
            iconClass: 'text-primary-green',
          },
          {
            title: 'Infrastructure Access',
            description: 'Our Facility Finder connects food innovators with pilot plants, labs, and co-manufacturing partners matched to their exact technical requirements.',
            icon: 'building',
            bgClass: 'bg-secondary-blue/10',
            iconClass: 'text-secondary-blue',
          },
          {
            title: 'Strategic Insights',
            description: 'From monthly Transition Pulses to deep-dive vertical reports, we translate ecosystem data into strategic clarity for leadership and innovation teams.',
            icon: 'bar-chart',
            bgClass: 'bg-secondary-yellow/10',
            iconClass: 'text-secondary-yellow',
          },
        ],
        team: [
          {
            name: 'Guido Laman',
            description: 'Project Leader',
            image: '/images/static-home-page/team-images-static-home-page-foodvalley/guido_image.jpg',
          },
          {
            name: 'Esther de Nijs',
            description: 'Platform Support',
            image: '/images/static-home-page/team-images-static-home-page-foodvalley/esther_image.jpg',
          },
          {
            name: 'Emmanuel Anom',
            description: 'Innovation Facility Specialist',
            image: '/images/static-home-page/team-images-static-home-page-foodvalley/emmanuel_image.jpg',
          },
          {
            name: 'Leo Koning',
            description: 'Community Manager Protein Shift',
            image: '/images/static-home-page/team-images-static-home-page-foodvalley/leo_image.jpg',
          },
          {
            name: 'Caroline Duivenvoorden',
            description: 'Community Manager Upcycling',
            image: '/images/static-home-page/team-images-static-home-page-foodvalley/caroline_image.jpg',
          },
          {
            name: 'Judith van der Horst',
            description: 'Community Manager Personalised Nutrition',
            image: '/images/static-home-page/team-images-static-home-page-foodvalley/judith_image.jpg',
          },
        ],
        values: [
          {
            title: 'Smart',
            description: 'Simple, fast, smart, efficient, solution-oriented.',
            icon: 'bolt',
          },
          {
            title: 'Challenging',
            description: 'Inviting, challenging, thinking further and discovering the need behind the question.',
            icon: 'compass-2',
          },
          {
            title: 'Energizing',
            description: 'Energetic, inspiring, taking the lead, offering opportunities.',
            icon: 'eco',
          },
          {
            title: 'Reliable',
            description: 'Trustworthy, form, stable, honest. Figures, facilities and funding.',
            icon: 'certificate',
          },
        ],
      }
    },
    methods: {
      goToRegistration () {
        this.$router.push('/register')
      },
      handleImgError (e) {
        e.target.style.display = 'none'
        e.target.parentElement.classList.add('fa-avatar-fallback')
      },
      onProductClick (productName, path) {
        trackFoodleapInteraction('Foodleap Product Card', productName)
        this.$router.push(path)
      },
      trackFoodleapCta,
    },
    mounted () {
      trackFoodleapPageView('/foodleap/about', 'Foodleap - About')

      this._scrollTracker = createScrollDepthTracker('Foodleap About')
      this._scrollTracker.start()

      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible')
              this._observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.1 }
      )
      this.$el.querySelectorAll('.animate-on-scroll').forEach(el => {
        this._observer.observe(el)
      })
      if (this.$route?.hash === '#contact') {
        this.$nextTick(() => {
          const el = document.getElementById('contact')
          if (el) el.scrollIntoView({ behavior: 'smooth' })
        })
      }
    },
    beforeUnmount () {
      if (this._observer) this._observer.disconnect()
      if (this._scrollTracker) this._scrollTracker.stop()
    },
  })
</script>

<style lang="scss">
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  // ── Keyframes ─────────────────────────────────────────────────────────────────

  @keyframes fa-fade-down {
    from {
      opacity: 0;
      transform: translateY(-16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fa-fade-up {
    from {
      opacity: 0;
      transform: translateY(24px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fa-float {
    0%, 100% {
      transform: translateY(0px) scale(1);
    }
    50% {
      transform: translateY(-20px) scale(1.05);
    }
  }

  @keyframes fa-float-slow {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-12px);
    }
  }

  @keyframes fa-shimmer {
    0% {
      background-position: -200% center;
    }
    100% {
      background-position: 200% center;
    }
  }

  // ── Scroll-reveal ─────────────────────────────────────────────────────────────

  .foodleap-about .animate-on-scroll {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.65s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.65s cubic-bezier(0.22, 1, 0.36, 1);

    &.delay-1 {
      transition-delay: 0.10s;
    }

    &.delay-2 {
      transition-delay: 0.20s;
    }

    &.delay-3 {
      transition-delay: 0.30s;
    }

    &.delay-4 {
      transition-delay: 0.40s;
    }

    &.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
  }

  // ── Component styles ──────────────────────────────────────────────────────────

  .foodleap-about {
    font-family: 'Inter', sans-serif;
    background-color: #ffffff;
    color: #21244A;

    $ps: #21244A;
    $pg: #5CCC61;
    $blg: #D9E2DA;
    $svg: #B5F167;
    $sy: #FFC83D;
    $sr: #EF374E;
    $sb: #70C1FF;

    // ── Color utilities ────────────────────────────────────────────────────────

    .bg-primary-slate {
      background-color: $ps;
    }

    .bg-primary-green {
      background-color: $pg;
    }

    .bg-brand-light-grey {
      background-color: $blg;
    }

    .bg-primary-green\/10 {
      background-color: rgba(92, 204, 97, 0.1);
    }

    .bg-secondary-blue\/5 {
      background-color: rgba(112, 193, 255, 0.05);
    }

    .bg-secondary-blue\/10 {
      background-color: rgba(112, 193, 255, 0.1);
    }

    .bg-secondary-yellow\/10 {
      background-color: rgba(255, 200, 61, 0.1);
    }

    .bg-primary-slate\/10 {
      background-color: rgba(33, 36, 74, 0.1);
    }

    .bg-primary-green\/5 {
      background-color: rgba(92, 204, 97, 0.05);
    }

    .bg-brand-light-grey\/20 {
      background-color: rgba(217, 226, 218, 0.2);
    }

    .bg-brand-light-grey\/30 {
      background-color: rgba(217, 226, 218, 0.3);
    }

    .bg-white\/5 {
      background-color: rgba(255, 255, 255, 0.05);
    }

    .bg-white\/10 {
      background-color: rgba(255, 255, 255, 0.1);
    }

    .text-primary-slate {
      color: $ps;
    }

    .text-primary-green {
      color: $pg;
    }

    .text-secondary-blue {
      color: $sb;
    }

    .text-secondary-yellow {
      color: $sy;
    }

    .text-primary-slate\/40 {
      color: rgba(33, 36, 74, 0.4);
    }

    .text-primary-slate\/50 {
      color: rgba(33, 36, 74, 0.5);
    }

    .text-primary-slate\/60 {
      color: rgba(33, 36, 74, 0.6);
    }

    .text-primary-slate\/70 {
      color: rgba(33, 36, 74, 0.7);
    }

    .text-white\/50 {
      color: rgba(255, 255, 255, 0.5);
    }

    .border-brand-light-grey {
      border-color: $blg;
    }

    .border-white\/10 {
      border-color: rgba(255, 255, 255, 0.1);
    }

    .shadow-primary-slate-20 {
      box-shadow: 0 20px 25px -5px rgba(33, 36, 74, 0.2), 0 8px 10px -6px rgba(33, 36, 74, 0.15);
    }

    // ── Hero ─────────────────────────────────────────────────────────────────

    .fa-hero-gradient {
      background: radial-gradient(circle at top right, $blg 0%, #FFFFFF 60%);
    }

    .fa-hero-badge {
      animation: fa-fade-down 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
    }

    .fa-hero-title {
      animation: fa-fade-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) 0.25s both;
    }

    .fa-hero-sub {
      animation: fa-fade-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) 0.4s both;
    }

    .fa-hero-btns {
      animation: fa-fade-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both;
    }

    .fa-blob-1 {
      animation: fa-float 9s ease-in-out infinite;
    }

    .fa-blob-2 {
      animation: fa-float-slow 11s ease-in-out infinite reverse;
    }

    // ── Buttons ───────────────────────────────────────────────────────────────

    .fa-btn-primary {
      position: relative;
      overflow: hidden;
      transition: transform 0.2s, box-shadow 0.2s;

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(105deg, transparent 40%, rgba(255, 255, 255, 0.15) 50%, transparent 60%);
        background-size: 200% 100%;
        opacity: 0;
        transition: opacity 0.2s;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 20px 40px rgba(33, 36, 74, 0.2);

        &::before {
          opacity: 1;
          animation: fa-shimmer 0.6s ease forwards;
        }
      }
    }

    .fa-btn-secondary {
      transition: transform 0.2s, background-color 0.2s;

      &:hover {
        transform: translateY(-2px);
        background-color: rgba(217, 226, 218, 0.8);
      }
    }

    .fa-arrow-icon {
      transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .fa-btn-primary:hover .fa-arrow-icon {
      transform: translateX(4px);
    }

    // ── Stat cards ────────────────────────────────────────────────────────────

    .fa-stat-card {
      transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 0.3s,
      background-color 0.2s;

      &:hover {
        transform: translateY(-4px);
        background-color: rgba(217, 226, 218, 0.5);
        box-shadow: 0 16px 40px rgba(33, 36, 74, 0.06);
      }
    }

    // ── Pillar cards ──────────────────────────────────────────────────────────

    .fa-pillar-card {
      transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 0.3s,
      border-color 0.3s;

      &:hover {
        transform: translateY(-6px);
        box-shadow: 0 24px 48px rgba(33, 36, 74, 0.08);
        border-color: rgba(92, 204, 97, 0.4);
      }
    }

    // ── Team cards ────────────────────────────────────────────────────────────

    .fa-team-card {
      transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);

      &:hover {
        transform: translateY(-4px);

        img {
          border-color: $pg;
        }
      }

      img {
        transition: border-color 0.3s;
      }
    }

    .fa-avatar-fallback {
      width: 6rem;
      height: 6rem;
      background: $blg;
      border-radius: 9999px;
    }

    // ── Contact cards ─────────────────────────────────────────────────────────

    .fa-contact-card {
      transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s, border-color 0.2s;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 16px 40px rgba(33, 36, 74, 0.07);
        border-color: rgba(92, 204, 97, 0.4);
      }
    }

    // ── Value cards ───────────────────────────────────────────────────────────

    .fa-value-card {
      transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1),
      background-color 0.2s,
      border-color 0.2s;

      &:hover {
        transform: translateY(-4px);
        background-color: rgba(255, 255, 255, 0.08);
        border-color: rgba(92, 204, 97, 0.3);
      }
    }

    // ── Product cards ─────────────────────────────────────────────────────────

    .fa-product-card {
      transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 0.3s,
      border-color 0.3s;

      &:hover {
        transform: translateY(-6px);
        box-shadow: 0 24px 48px rgba(33, 36, 74, 0.08);
        border-color: rgba(92, 204, 97, 0.4);
      }
    }
  }
</style>
